import React, { useRef, useEffect } from 'react';
import './index.scss';
import { MapState } from '@/utils/constant';
import { useDispatch } from 'react-redux';
import ThreeMonitor, {
  ThreeMonitorRef,
} from '@/components/ThreeMonitor/Monitor/ThreeMonitor-FuncParseRewrite';
import { parseBinBuffer } from '@/utils/localViewBin';
import { mqttPublish } from '@/utils/util';
import { trafficLightInfo } from '@/redux/reducers/trafficLight';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { CloseCircleOutlined } from '@ant-design/icons';
import { request } from '@/fetch/core';
import { HttpStatusCode } from '@/fetch/core/constant';
import { message } from 'antd';

// URL 带 mock3d=1 时，3D 模块改用本地录制的 mock 数据（localMode，不连真实 WS），仅调试用
const queryParams = new URLSearchParams(window.location.search);
const isMock3d = queryParams.get('mock3d') === '1';
const mockBinName = queryParams.get('mockBin') || 'recorded_data_with_gates.bin';

export interface ThreeMonitorMapProps {
  runMapState: MapState;
  vehicleName: string;
  takeOverBySelf: boolean;
  cockpitUserName: string;
  localViewStatus: 'CLOSE' | 'OPEN';
  signalQuality: 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR';
}

const ThreeMonitorMap = (props: ThreeMonitorMapProps) => {
  const { vehicleName, takeOverBySelf, localViewStatus, signalQuality } = props;
  const recordDiffTimeDataRef = useRef<any[]>([]);
  const threeRef = useRef<ThreeMonitorRef>(null);
  const dispatch = useDispatch();

  // mock3d 模式：加载本地录制 bin，10Hz 循环喂给 3D 模块
  useEffect(() => {
    if (!isMock3d) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;
    let idx = 0;
    (async () => {
      try {
        const res = await fetch(`/constant/${mockBinName}`);
        if (!res.ok) {
          console.error(
            `[mock3d] 加载失败: /constant/${mockBinName} (${res.status} ${res.statusText})`
          );
          return;
        }
        const buf = await res.arrayBuffer();
        const frames = parseBinBuffer(buf);
        if (cancelled || frames.length === 0) return;
        timer = setInterval(() => {
          threeRef.current?.feedData(frames[idx % frames.length].buffer);
          idx++;
        }, 100); // 10Hz
      } catch (err) {
        console.error('[mock3d] 加载 mock 数据失败:', err);
      }
    })();
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);
  const cockpitNumber = useSelector(
    (state: RootState) => state.newCommon?.cockpitNumber
  );
  const showCloseBtnQuality = ['FAIR', 'POOR', 'GOOD'];
  const className =
    takeOverBySelf &&
    localViewStatus === 'OPEN' &&
    showCloseBtnQuality.includes(signalQuality)
      ? 'color-red'
      : '';

  // 定时器检查recordDiffTimeData条数
  useEffect(() => {
    const timer = setInterval(() => {
      if (recordDiffTimeDataRef.current.length > 20) {
        // 发送MQTT消息
        mqttPublish(
          process.env.JDX_APP_SCREEN_DATA + '/' + props.cockpitUserName,
          JSON.stringify({
            header: {
              messageType: 'LOCAL_VIEW_DATA',
              requestId: Date.now(),
              clientName: cockpitNumber,
              requestTime: Date.now(),
              needResponse: false,
            },
            data: {
              userName: props.cockpitUserName,
              vehicleName: vehicleName,
              recordList: recordDiffTimeDataRef.current.slice(0, 20),
              cockpitNumber: cockpitNumber,
            },
          })
        );
        // 清空数据
        recordDiffTimeDataRef.current = [];
      }
    }, 3000);

    return () => {
      clearInterval(timer);
    };
  }, [props.cockpitUserName, vehicleName]);

  const updateTrafficLight = (trafficData: any) => {
    dispatch(trafficLightInfo(trafficData));
  };

  const operate3d = async () => {
    const res = await request({
      method: 'POST',
      path: '/monitor/web/cockpit/operate3d',
      body: {
        vehicleName: vehicleName,
        operateType: localViewStatus === 'CLOSE' ? 'OPEN' : 'CLOSE',
      },
    });
    if (res.code === HttpStatusCode.Success) {
      message.success(`${localViewStatus === 'CLOSE' ? '开启' : '关闭'}3D成功`);
    } else {
      message.error(
        res.message || `${localViewStatus === 'CLOSE' ? '开启' : '关闭'}3D失败`
      );
    }
  };

  return (
    <div id="localview-container">
      {takeOverBySelf && localViewStatus === 'OPEN' && (
        <div
          className={`localview-close-open-btn ${className}`}
          onClick={() => operate3d()}
        >
          {showCloseBtnQuality.includes(signalQuality) && <span>关闭</span>}
          <span>
            <CloseCircleOutlined style={{ color: 'white', fontSize: '20px' }} />
          </span>
        </div>
      )}
      {vehicleName && (
        <>
          <ThreeMonitor
            ref={threeRef}
            key={vehicleName}
            carNumber={vehicleName}
            url={process.env.JDX_APP_MONITOR_PROXY_URL + vehicleName}
            localMode={isMock3d}
            updateTrafficLight={updateTrafficLight}
            handleRecordDiffTime={(val: any) => {
              recordDiffTimeDataRef.current = [
                ...recordDiffTimeDataRef.current,
                val,
              ];
            }}
          />
          <div
            className="open-localview-btn"
            style={{
              display:
                localViewStatus === 'CLOSE' && takeOverBySelf && !isMock3d
                  ? 'flex'
                  : 'none',
            }}
            onClick={operate3d}
          >
            <img
              src={require('@/assets/images/singleVehicle/camera.png')}
              className="camera"
            ></img>
            <div className="open-tip">点击开启3D</div>
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(ThreeMonitorMap);
